import SurfaceCard from "@/components/primitives/SurfaceCard";
import { DistBar, NoteLine, SectionHead, StatGrid, hasStatValue } from "@/components/organization/kit";
import VerifiedAgentCountLabel from "@/components/organization/overview/VerifiedAgentCountLabel";

interface AgentPerformanceProps {
  /** Active agent headcount. */
  activeAgents?: string | null;
  /** True when `activeAgents` is the platform-verified count, not the self-reported one. */
  activeAgentsVerified?: boolean;
  /** Agent retention rate (e.g. "91%"). */
  retention?: string | null;
  /** Average agent tenure (e.g. "5.4 yrs"). */
  avgTenure?: string | null;
  /** New-agent success rate (e.g. "78%"). */
  newAgentSuccess?: string | null;
  /** Share of new agents who hit their stated income goal (e.g. "74%"). */
  incomeGoalHitRate?: string | null;
  /** Anonymized share of agents per income band (percentage, read as stored). */
  distribution: { band: string; pct: number }[];
}

/**
 * Agent outcomes — handled with restraint. We surface the operational outcome stats
 * (headcount, retention, tenure, new-agent success, income-goal hit rate), each
 * omitted when absent, plus the anonymized income-distribution shape.
 * We never publish per-agent GCI dollar headlines.
 */
const AgentPerformance = ({
  activeAgents,
  activeAgentsVerified,
  retention,
  avgTenure,
  newAgentSuccess,
  incomeGoalHitRate,
  distribution,
}: AgentPerformanceProps) => {
  const items = [
    { label: activeAgentsVerified ? <VerifiedAgentCountLabel label="Agents on Compaire" /> : "Active agents", value: activeAgents },
    { label: "Retention", value: retention },
    { label: "Avg. tenure", value: avgTenure },
    { label: "New-agent success", value: newAgentSuccess },
    { label: "Hit income goal", value: incomeGoalHitRate },
  ];

  const hasStats = items.some((s) => hasStatValue(s.value));
  const hasDistribution = distribution.length > 0;
  if (!hasStats && !hasDistribution) return null;

  return (
    <SurfaceCard padding="lg" className="rounded-2xl">
      <SectionHead kicker="Agent performance" title="Agent outcomes" />

      {hasStats && <StatGrid items={items} cols={5} />}

      {hasDistribution && (
        <div className={hasStats ? "mt-6" : undefined}>
          <div className="eyebrow mb-3.5 text-[10px] text-ink-500">Income distribution across the team</div>
          <DistBar data={distribution} />
          <NoteLine>
            Shown as the share of agents by income band — Compaire keeps individual agent income private and
            never publishes per-agent figures.
          </NoteLine>
        </div>
      )}
    </SurfaceCard>
  );
};

export default AgentPerformance;
